/* eslint-disable import/no-cycle */
/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */

import { v4 as uuidv4 } from 'uuid';
import isString from 'lodash/isString';
import isObject from 'lodash/isObject';

import { FORM_ADVANCED_SECTIONS } from '../schemas/formAdvancedSections';
import { FORM_COMPONENT_BASIC_INPUT_TYPE } from '../schemas/formComponentInputTypeSchema';
import { FORM_COMPONENT_SCHEMAS } from '../schemas/formFieldSchema';
import { FormSectionT, FormFieldT, FormItemT } from '../types';
import { isFieldSchema, isSectionSchema } from '../utils';

function findOrCreateSection(
  items: FormItemT[],
  sectionName: string,
  isAdvanced: boolean,
  idGenerator: () => string,
  sectionTitle?: Record<string, string>,
): FormSectionT {
  let section = items.find((item): item is FormSectionT => !isFieldSchema(item) && item.section === sectionName);

  if (!section) {
    section = {
      id: idGenerator(),
      section: sectionName,
      sectionTitle: sectionTitle || { en: sectionName },
      isAdvanced,
      fields: [],
    };
    items.push(section);
  } else {
    if (isAdvanced && !section.isAdvanced) {
      section.isAdvanced = true;
    }
    if ((!section.sectionTitle || Object.keys(section.sectionTitle).length === 0) && sectionTitle) {
      section.sectionTitle = sectionTitle;
    }
  }
  return section;
}

function applyAdvancedSectionLogic(section: FormSectionT, formBuilderSchemas: typeof FORM_COMPONENT_SCHEMAS): FormSectionT {
  const sectionName = section.section;
  const isCanadianBank = sectionName === FORM_ADVANCED_SECTIONS.canadianBank;
  const isAmericanBank = sectionName === FORM_ADVANCED_SECTIONS.americanBank;
  const isSin = sectionName === FORM_ADVANCED_SECTIONS.sin;

  if (isCanadianBank || isAmericanBank || isSin) {
    const currentFields = [...section.fields];

    let defaultFieldsKey: string;
    if (isCanadianBank) {
      defaultFieldsKey = FORM_ADVANCED_SECTIONS.canadianBank;
    } else if (isAmericanBank) {
      defaultFieldsKey = FORM_ADVANCED_SECTIONS.americanBank;
    } else {
      defaultFieldsKey = 'sinAdvanced';
    }

    const defaultFieldsData = formBuilderSchemas[defaultFieldsKey as keyof typeof formBuilderSchemas];
    // Deep copy to prevent mutation of the original schema
    const templateDefaultFields = JSON.parse(JSON.stringify(defaultFieldsData?.defaultValue || [])) as FormFieldT[];

    const userFieldsOnly = currentFields.filter(isFieldSchema);

    const disabledId = templateDefaultFields?.find((field) => field?.disabledId)?.disabledId;

    const mergedArray: FormFieldT[] = [];
    const isFollowUp = userFieldsOnly?.some((field) => field?.isFollowUp);

    userFieldsOnly.forEach((item, index) => {
      const secondItemCurrentIndex = templateDefaultFields[index];
      if (isFollowUp && secondItemCurrentIndex) {
        secondItemCurrentIndex.isFollowUp = true;
      }
      if (secondItemCurrentIndex?.isFrontEndOnly === true) {
        mergedArray.push({ ...secondItemCurrentIndex, id: uuidv4() });
      }
      mergedArray.push(item);

      const nextIndex = index + 1;

      const secondItemNextIndex = templateDefaultFields[nextIndex];
      if (isFollowUp && secondItemNextIndex) {
        secondItemNextIndex.isFollowUp = true;
      }
      if (secondItemNextIndex?.isFrontEndOnly === true && nextIndex === userFieldsOnly.length) {
        mergedArray.push({ ...secondItemNextIndex, disabled: true });
      }
    });

    templateDefaultFields.forEach((field, index) => {
      const isEnabled = templateDefaultFields.length === mergedArray.length;
      const userField = mergedArray[index] ?? field;
      mergedArray[index] = {
        ...userField,
        ...(field?.disabledId && !field?.isFrontEndOnly && { disabled: !isEnabled }),
        ...(field?.disabledId && field?.isFrontEndOnly && { disabled: false }),
        ...(field?.disabledId && { disabledId }),
      };
    });

    return { ...section, fields: [...mergedArray] };
  }

  const mappedFields = section.fields?.map((item) => {
    if (isFieldSchema(item)) {
      return item;
    }
    return applyAdvancedSectionLogic(item, formBuilderSchemas);
  });
  return { ...section, fields: mappedFields || [] };
}

function processFollowUpList(followUpFields: FormFieldT[], parent: FormFieldT, idGenerator: () => string): FormItemT[] {
  const result: FormItemT[] = [];
  const sectionsMap = new Map<string, FormSectionT>();
  const MAX_SECTION_MAP_SIZE = 10000;

  followUpFields.forEach((field) => {
    const currentFieldSectionName = isString(field.section) && field.section.trim() ? field.section.trim() : null;

    const isAdvancedData = currentFieldSectionName
      ? Object.values(FORM_ADVANCED_SECTIONS).includes(currentFieldSectionName as FORM_ADVANCED_SECTIONS)
      : false;

    const defaultFieldSchema = FORM_COMPONENT_SCHEMAS[field.inputType as keyof typeof FORM_COMPONENT_SCHEMAS]?.defaultValue;
    const defaultField = defaultFieldSchema ? { ...(defaultFieldSchema as FormFieldT) } : {};

    const newField: FormFieldT = {
      ...defaultField,
      ...field,
      id: field.id || idGenerator(),
    };

    if (newField.parentSection !== parent.parentSection && isString(newField.parentSection) && newField.parentSection.trim()) {
      const parentSectionName = newField.parentSection.trim();
      const subsectionKey = `${parentSectionName}::${currentFieldSectionName || 'unnamed_subsection'}`;

      let targetSubSection = sectionsMap.get(subsectionKey);

      if (!targetSubSection) {
        if (sectionsMap.size > MAX_SECTION_MAP_SIZE) {
          // eslint-disable-next-line no-console
          console.warn('Skipping subsection due to Map size overflow', subsectionKey);
          return;
        }
        targetSubSection = {
          id: idGenerator(),
          section: currentFieldSectionName || 'unnamed_subsection',
          sectionTitle: newField.sectionTitle || { en: currentFieldSectionName || 'unnamed_subsection' },
          isAdvanced: isAdvancedData,
          fields: [],
          optionId: field.optionId,
        };
        sectionsMap.set(subsectionKey, targetSubSection);
        result.push(targetSubSection);
      }
      targetSubSection.fields.push(newField);
    } else if (currentFieldSectionName) {
      result.push(newField);
    } else {
      result.push(newField);
    }
  });
  const finalSections = result.map((section) =>
    isSectionSchema(section) ? applyAdvancedSectionLogic(section, FORM_COMPONENT_SCHEMAS) : section,
  );

  return finalSections;
}

export function generateSectionSchema({ currentTemplate }: { currentTemplate: FormFieldT[] }): FormSectionT[] {
  const idGen = uuidv4;

  const groupedSections = currentTemplate.reduce<FormSectionT[]>((acc, field) => {
    const sectionName =
      isString(field.section) && field.section.trim() ? field.section.trim() : `generated_section_${acc.length + 1}`;

    const sectionTitle = (field.sectionTitle && Object.keys(field.sectionTitle).length > 0
      ? field.sectionTitle
      : { en: sectionName }) as Record<string, string>;

    const isAdvancedData = Object.values(FORM_ADVANCED_SECTIONS).includes(sectionName as FORM_ADVANCED_SECTIONS);

    const defaultFieldSchema = FORM_COMPONENT_SCHEMAS[field.inputType as keyof typeof FORM_COMPONENT_SCHEMAS]?.defaultValue;
    const defaultField = defaultFieldSchema ? { ...(defaultFieldSchema as FormFieldT) } : {};

    const processedOptionList = field.optionList?.map((option, index) => ({
      ...option,
      text: isObject(option.text) && option.text !== null ? { ...option.text } : option.text,
      key:
        field.inputType === FORM_COMPONENT_BASIC_INPUT_TYPE.yesNo && option.text?.en
          ? option.text.en
          : String.fromCharCode(65 + index).toUpperCase(),
    }));

    const newField: FormFieldT = {
      ...defaultField,
      ...field,
      id: field.id || idGen(),
      optionList: processedOptionList,
    };

    if (!newField.sectionTitle || Object.keys(newField.sectionTitle).length === 0) {
      newField.sectionTitle = sectionTitle;
    }

    if (newField.inputType === FORM_COMPONENT_BASIC_INPUT_TYPE.yesNo) {
      if (newField.followUpQuestionList && newField.followUpQuestionList.length > 0) {
        newField.followUpQuestionList = processFollowUpList(newField.followUpQuestionList as FormFieldT[], newField, idGen);
      } else {
        newField.followUpQuestionList = [];
      }
    }
    if (field.parentSection && isString(field.parentSection) && field.parentSection.trim()) {
      const parentSectionName = field.parentSection.trim();
      const parentSectionTitle = (field.parentSectionTitle && Object.keys(field.parentSectionTitle).length > 0
        ? field.parentSectionTitle
        : { en: parentSectionName }) as Record<string, string>;
      const parentSection = findOrCreateSection(acc, parentSectionName, false, idGen, parentSectionTitle);
      const targetSubSection = findOrCreateSection(parentSection.fields, sectionName, isAdvancedData, idGen, sectionTitle);

      targetSubSection.fields.push(newField);
    } else {
      const targetSection = findOrCreateSection(acc, sectionName, isAdvancedData, idGen, sectionTitle);
      targetSection.fields.push(newField);
    }
    return acc;
  }, []);

  const finalSections = groupedSections.map((section) => applyAdvancedSectionLogic(section, FORM_COMPONENT_SCHEMAS));

  return finalSections;
}
